import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import { SPACE_RECORDS_SELECTION_TARGET } from '../../../constants'
import type { Plugin } from '../plugin'

const SUBMIT_CANCELLATION_REASON = {
  noContext: 'No scenario context available',
  noSelectedRecords: 'No Space records selected for the bulk comment',
  noUpdate: 'No bulk comment in progress',
  notUpdated: 'None of the selected records could be updated',
} as const

/**
 * Applies the edited comment to every selected record.
 */
export const executeSubmit = store.effects.create(
  (feature: Plugin['feature'], plugin: Plugin) =>
    feature.actions.submit.request.observe().pipe(
      rxjs.withLatestFrom(
        feature.selectors.isUpdating.asObservable,
        feature.selectors.comment.asObservable,
        plugin.collection.feature.selectors.context.asObservable,
        plugin.collection.plugins.selectionPlugin.feature.selectors.getAllSelectedIds(
          {
            target: SPACE_RECORDS_SELECTION_TARGET,
          },
        ).asObservable,
      ),
      rxjs.mergeMap(([action, isUpdating, comment, context, selectedIds]) => {
        if (!isUpdating) {
          return rxjs.of(
            feature.actions.submit.cancellation
              .from(action)
              .create({ reason: SUBMIT_CANCELLATION_REASON.noUpdate }),
          )
        }

        if (!ents.core.admin.Context.ForScenario.isForScenario(context)) {
          return rxjs.of(
            feature.actions.submit.cancellation
              .from(action)
              .create({ reason: SUBMIT_CANCELLATION_REASON.noContext }),
          )
        }

        const spaceRecordIds = selectedIds.filter(
          ents.extra.space.Record.Id.isId,
        )

        if (!spaceRecordIds.length) {
          return rxjs.of(
            feature.actions.submit.cancellation
              .from(action)
              .create({ reason: SUBMIT_CANCELLATION_REASON.noSelectedRecords }),
          )
        }

        return plugin.api
          .update({ comment, context: { ...context, spaceRecordIds } })
          .pipe(
            rxjs.map(affectedIds => {
              // The backend returns only the records it actually updated, so
              // an empty result means the comment was applied to nothing.
              if (!affectedIds.length) {
                return feature.actions.submit.cancellation
                  .from(action)
                  .create({ reason: SUBMIT_CANCELLATION_REASON.notUpdated })
              }

              return feature.actions.submit.success.from(action).create()
            }),
            rxjs.catchError((error: Error) =>
              rxjs.of(
                feature.actions.submit.error.from(action).create({ error }),
              ),
            ),
          )
      }),
    ),
)
